import { Autocomplete, Box, Chip, Stack, TextField, Typography } from "@mui/material";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { EventTypeOption } from "./fluss-builder-types";

export interface EventTypeSelectProps {
    value: string | null;
    onChange: (value: string | null) => void;
    options: EventTypeOption[];
    loading?: boolean;
    disabled?: boolean;
    label?: ReactNode | null;
}

export const EventTypeSelect = ({
    value,
    onChange,
    options,
    loading = false,
    disabled = false,
    label,
}: EventTypeSelectProps) => {
    const { t } = useTranslation();
    const selected = options.find((option) => option.id === value || option.name === value) || null;
    const displayLabel = (option: EventTypeOption) => {
        const translated = String(t(option.label || option.name));
        return translated && translated !== option.label ? translated :
            (option.label && !/^[a-z][a-z0-9_]*(\.[a-z0-9_-]+)+$/i.test(option.label) ? option.label : option.name);
    };
    // PRIM-032 (round 9): keep the MUI input label as the single associated field label.
    const inputLabel = label === undefined
        ? t("fluss.builder.event_type.label", { defaultValue: "Event type" })
        : label;

    return (
        <Autocomplete
            size="small"
            fullWidth
            options={options}
            value={selected}
            loading={loading}
            disabled={disabled}
            // PRIM-030 (round 9): selected value shows the human label; technical name stays in options.
            getOptionLabel={displayLabel}
            getOptionDisabled={(option) => !option.active}
            isOptionEqualToValue={(option, current) => option.id === current.id}
            noOptionsText={t("fluss.builder.event_type.empty", { defaultValue: "No event types found." })}
            onChange={(_, option) => onChange(option?.id || null)}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={inputLabel}
                    helperText={selected?.name || undefined}
                    FormHelperTextProps={{ sx: { overflowWrap: "anywhere" } }}
                    sx={{ minWidth: 0, "& .MuiInputBase-input": { textOverflow: "ellipsis" } }}
                    inputProps={{
                        ...params.inputProps,
                        "aria-label": String(t("fluss.builder.event_type.search", {
                            defaultValue: "Search event types",
                        })),
                    }}
                />
            )}
            renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                    <Stack spacing={0.25} sx={{ minWidth: 0, width: "100%" }}>
                        <Typography
                            variant="body2" sx={{ overflowWrap: "anywhere" }}
                        >
                            {displayLabel(option)}
                        </Typography>
                        <Typography component="code" color="text.secondary" sx={{ fontFamily: "monospace", fontSize: 11, overflowWrap: "anywhere" }}>
                            {option.name}
                        </Typography>
                        {!option.active && (
                            <Chip
                                size="small"
                                color="warning"
                                label={t("fluss.builder.event_type.inactive", { defaultValue: "Inactive" })}
                            />
                        )}
                    </Stack>
                </Box>
            )}
        />
    );
};

export default EventTypeSelect;
